/**
 * ServiceTrust - Reusable trust/credentials section for service pages
 * Displays why customers should trust the service
 */

import { TrustBadge } from '../../types';

interface ServiceTrustProps {
  serviceName: string;
  badges?: TrustBadge[];
  contactSectionId?: string;
}

const DEFAULT_SERVICE_BADGES: TrustBadge[] = [
  {
    icon: 'ri-24-hours-fill',
    title: '24/7 Emergency Response',
    description: 'Available around the clock for emergencies',
  },
  {
    icon: 'ri-verified-badge-fill',
    title: 'IICRC Certified',
    description: 'Industry-certified technicians',
  },
  {
    icon: 'ri-shield-check-fill',
    title: 'Licensed & Insured',
    description: 'Fully licensed and insured',
  },
  {
    icon: 'ri-file-list-3-fill',
    title: 'Insurance Assistance',
    description: 'We work with your insurance',
  },
];

export default function ServiceTrust({
  serviceName,
  badges = DEFAULT_SERVICE_BADGES,
  contactSectionId = 'contact',
}: ServiceTrustProps) {
  const scrollToContact = () => {
    const element = document.getElementById(contactSectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">
            Why Choose Us for {serviceName}
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-300 max-w-3xl mx-auto">
            Professional service you can trust
          </p>
        </div>

        {/* Trust Badges Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {badges.map((badge, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center hover:bg-white/15 transition-colors"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-sky-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className={`${badge.icon} text-xl sm:text-2xl text-white`}></i>
              </div>
              <h3 className="text-sm sm:text-base md:text-lg font-bold text-white mb-1 sm:mb-2">
                {badge.title}
              </h3>
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                {badge.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="mt-10 sm:mt-12 md:mt-16 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
          <a
            href="tel:3853406632"
            className="w-full sm:w-auto text-center bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill mr-2"></i>
            Call (385) 340-6632
          </a>
          <button
            onClick={scrollToContact}
            className="w-full sm:w-auto bg-white text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 hover:brightness-90 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Get Free Estimate
          </button>
        </div>
      </div>
    </section>
  );
}
